import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import AdminNavbar from "../Layout/AdminNavbar";
import ManagerNavbar from "../Layout/ManagerNavbar";
import EmployeeNavbar from "../Layout/EmployeeNavbar";
import API_BASE_URL from "../config";
import { format, formatDistanceToNow } from "date-fns";

/* ══════════════════════════════════════════════════════════════
   SPRINT CONSTANTS  (same as SprintPanel)
══════════════════════════════════════════════════════════════ */
const STATUS_BADGE = {
  PLANNING: { bg: "#dbeafe", color: "#1e40af", label: "Planning", border: "#93c5fd" },
  ACTIVE: { bg: "#dcfce7", color: "#166534", label: "Active", border: "#86efac" },
  COMPLETED: { bg: "#f3f4f6", color: "#374151", label: "Completed", border: "#d1d5db" },
  CANCELLED: { bg: "#fee2e2", color: "#991b1b", label: "Cancelled", border: "#fca5a5" },
};

const SPRINT_KANBAN_COLS = [
  { key: "PLANNING", label: "Planning", icon: "📋", color: "#1e40af", bg: "#eff6ff", border: "#bfdbfe" },
  { key: "ACTIVE", label: "Active", icon: "⚡", color: "#166534", bg: "#f0fdf4", border: "#bbf7d0" },
  { key: "COMPLETED", label: "Completed", icon: "✅", color: "#374151", bg: "#f9fafb", border: "#e5e7eb" },
  { key: "CANCELLED", label: "Cancelled", icon: "🚫", color: "#991b1b", bg: "#fff5f5", border: "#fecaca" },
];

const TASK_STATUS_COLS = [
  { key: "PENDING", label: "TO DO", color: "#6b7280", icon: "bi-circle-fill" },
  { key: "IN_PROGRESS", label: "IN PROGRESS", color: "#1d4ed8", icon: "bi-arrow-repeat" },
  { key: "READY TO REVIEW", label: "READY TO REVIEW", color: "#92400e", icon: "bi-eye" },
  { key: "READY TO TEST", label: "READY TO TEST", color: "#0ea5e9", icon: "bi-clipboard-check" },
  { key: "IN TESTING", label: "IN TESTING", color: "#f59e0b", icon: "bi-bug" },
  { key: "READY TO UPDATE", label: "READY TO UPDATE", color: "#10b981", icon: "bi-arrow-up-circle" },
  { key: "WAITING FOR CUSTOMER", label: "WAITING FOR CUSTOMER", color: "#d946ef", icon: "bi-person-lines-fill" },
  { key: "COMPLETED", label: "DONE", color: "#166534", icon: "bi-check-circle-fill" },
  { key: "CANCELLED", label: "CANCELLED", color: "#f43f5e", icon: "bi-x-circle-fill" },
];

const PRIORITY_COLOR = { High: "#ef4444", Medium: "#f59e0b", Low: "#22c55e", Critical: "#7c3aed" };

/* ══════════════════════════════════════════════════════════════
   UTILITY HELPERS
══════════════════════════════════════════════════════════════ */
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtShort = (d) => d ? new Date(d).toLocaleDateString("en-GB") : "—";
const pct = (s) => !s || s.totalTasks === 0 ? 0 : Math.round((s.completedTasks / s.totalTasks) * 100);
const daysLeft = (sprint) => sprint?.endDate ? Math.ceil((new Date(sprint.endDate) - new Date()) / 86400000) : null;

const normalizeStatusKey = (s) => (s || "").toUpperCase().replace(/_/g, " ");

const getAvatarColor = (name = "") => {
  const colors = ["#09d0f3", "#0d6efd", "#6f42c1", "#198754", "#0dcaf0", "#ffc107", "#fd7e14", "#dc3545", "#8b5cf6", "#22c55e", "#14b8a6", "#3b82f6", "#6366f1"];
  let hash = 5381;
  for (let i = 0; i < name.length; i++) hash = ((hash << 5) + hash) + name.charCodeAt(i);
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (n = "") =>
  n.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || "").join("");

const getPriorityClass = (p) => {
  switch ((p || "").toLowerCase()) {
    case "high": case "critical": return "task-card-emp__priority--high";
    case "medium": return "task-card-emp__priority--medium";
    case "low": return "task-card-emp__priority--low";
    default: return "task-card-emp__priority--none";
  }
};

const getAvatarClass = (taskId) => `av-${((Number(taskId) || 0) % 6) + 1}`;
const getAssigneeInitial = (a) => (a?.firstName || a?.lastName || "?").charAt(0).toUpperCase();

const normalizeTask = (t) => ({
  ...t,
  assignedTo: t.assigneeId ? { id: t.assigneeId, firstName: t.assigneeFirstName, lastName: t.assigneeLastName, photoPath: t.assigneePhotoPath } : null,
  reporter: t.reporterId ? { id: t.reporterId, firstName: t.reporterFirstName, lastName: t.reporterLastName } : null,
  project: t.projectId ? { id: t.projectId, name: t.projectName } : null,
  sprint: t.sprintId ? { id: t.sprintId, name: t.sprintName, sprintNumber: t.sprintNumber } : null,
  team: t.teamId ? { id: t.teamId, name: t.teamName } : null,
});

/* ══════════════════════════════════════════════════════════════
   SMALL INTERNAL COMPONENTS
══════════════════════════════════════════════════════════════ */

/* ── ConfirmModal ──────────────────────────────────────────── */
const ConfirmModal = ({ open, onClose, onConfirm, title, message }) => {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 2010,
        width: "min(420px,90vw)", background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden"
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#dc3545,#e85d6b)" }} />
        <div style={{ padding: "24px 24px 20px" }}>
          <h5 style={{ fontWeight: 700, margin: "0 0 8px", fontSize: 16 }}>{title}</h5>
          <p style={{ color: "#6b7280", margin: "0 0 20px", fontSize: 14 }}>{message}</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={onConfirm} style={{ padding: "8px 18px", borderRadius: 7, background: "#dc3545", color: "#fff", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Delete</button>
            <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 7, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", cursor: "pointer", fontSize: 13 }}>Cancel</button>
          </div>
        </div>
      </div>
    </>
  );
};

/* ── CompleteSprintModal ───────────────────────────────────── */
const CompleteSprintModal = ({ open, onClose, sprint, availableSprints, onConfirm, completing }) => {
  const [moveTarget, setMoveTarget] = useState("backlog");
  const [targetSprintId, setTargetSprintId] = useState("");

  useEffect(() => { if (open) { setMoveTarget("backlog"); setTargetSprintId(""); } }, [open]);
  if (!open || !sprint) return null;

  const incompleteCount = sprint.totalTasks - sprint.completedTasks - (sprint.cancelledTasks || 0);
  const canConfirm = !completing && !(moveTarget === "sprint" && !targetSprintId);

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 2010,
        width: "min(480px,92vw)", background: "#fff", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.22)", overflow: "hidden"
      }}>
        <div style={{ background: "linear-gradient(135deg,#0052cc 0%,#2684ff 100%)", padding: "18px 22px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h5 style={{ color: "#fff", margin: 0, fontWeight: 700, fontSize: 15 }}>Complete Sprint</h5>
              <p style={{ color: "rgba(255,255,255,.75)", margin: "3px 0 0", fontSize: 12 }}>{sprint.name} · Sprint {sprint.sprintNumber}</p>
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#fff", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
          </div>
        </div>

        <div style={{ padding: "22px 22px 18px" }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
            {[
              { label: "Completed", val: sprint.completedTasks, bg: "#f0fdf4", border: "#bbf7d0", color: "#16a34a" },
              { label: "Incomplete", val: incompleteCount, bg: incompleteCount > 0 ? "#fff7ed" : "#f9fafb", border: incompleteCount > 0 ? "#fed7aa" : "#e5e7eb", color: incompleteCount > 0 ? "#ea580c" : "#9ca3af" },
              { label: "Total", val: sprint.totalTasks, bg: "#f9fafb", border: "#e5e7eb", color: "#374151" },
            ].map(x => (
              <div key={x.label} style={{ flex: 1, background: x.bg, border: `1px solid ${x.border}`, borderRadius: 9, padding: "12px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: x.color, lineHeight: 1 }}>{x.val}</div>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>{x.label}</div>
              </div>
            ))}
          </div>

          {incompleteCount > 0 ? (
            <>
              <p style={{ fontWeight: 600, fontSize: 13, color: "#374151", margin: "0 0 12px" }}>
                Where should the {incompleteCount} incomplete task{incompleteCount !== 1 ? "s" : ""} go?
              </p>
              <label style={{
                display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderRadius: 9,
                border: `2px solid ${moveTarget === "backlog" ? "#0052cc" : "#e5e7eb"}`,
                background: moveTarget === "backlog" ? "#eff6ff" : "#fff", cursor: "pointer", marginBottom: 10
              }}>
                <input type="radio" name="csmTarget" value="backlog" checked={moveTarget === "backlog"} onChange={() => setMoveTarget("backlog")} style={{ accentColor: "#0052cc", width: 15, height: 15 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>📋 Move to Backlog</div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>Tasks will be unassigned from any sprint</div>
                </div>
              </label>
              <label style={{
                display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 14px", borderRadius: 9,
                border: `2px solid ${moveTarget === "sprint" ? "#0052cc" : "#e5e7eb"}`,
                background: moveTarget === "sprint" ? "#eff6ff" : "#fff", cursor: "pointer"
              }}>
                <input type="radio" name="csmTarget" value="sprint" checked={moveTarget === "sprint"} onChange={() => setMoveTarget("sprint")} style={{ accentColor: "#0052cc", width: 15, height: 15, marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#374151", marginBottom: moveTarget === "sprint" ? 8 : 0 }}>⚡ Move to another Sprint</div>
                  {moveTarget === "sprint" && (
                    availableSprints.length === 0
                      ? <p style={{ fontSize: 12, color: "#ef4444", margin: 0 }}>No other planning/active sprints available</p>
                      : <select value={targetSprintId} onChange={e => setTargetSprintId(e.target.value)} onClick={e => e.stopPropagation()}
                        style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, background: "#fff" }}>
                        <option value="">Select a sprint…</option>
                        {availableSprints.map(s => (
                          <option key={s.id} value={s.id}>Sprint {s.sprintNumber} — {s.name} ({s.status === "ACTIVE" ? "Active" : "Planning"})</option>
                        ))}
                      </select>
                  )}
                </div>
              </label>
            </>
          ) : (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 9, padding: "16px", textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>🎉</div>
              <p style={{ margin: 0, fontWeight: 600, color: "#16a34a", fontSize: 13 }}>All tasks are completed! Great work.</p>
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "0 22px 20px" }}>
          <button onClick={() => onConfirm(moveTarget === "sprint" ? (targetSprintId || null) : null)} disabled={!canConfirm}
            style={{ padding: "9px 22px", borderRadius: 8, background: completing ? "#86efac" : "#16a34a", color: "#fff", border: "none", cursor: canConfirm ? "pointer" : "not-allowed", fontWeight: 600, fontSize: 13, opacity: !canConfirm ? 0.65 : 1 }}>
            {completing ? "Completing…" : "✔ Complete Sprint"}
          </button>
          <button onClick={onClose} disabled={completing}
            style={{ padding: "9px 20px", borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", cursor: completing ? "not-allowed" : "pointer", fontWeight: 500, fontSize: 13 }}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

const ActiveSprintPickerModal = ({ open, onClose, activeSprints, onSelect }) => {
  if (!open) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000 }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 2010,
        width: "min(560px,92vw)", background: "#fff", borderRadius: 14,
        boxShadow: "0 20px 60px rgba(0,0,0,0.22)", overflow: "hidden",
      }}>
        <div style={{ background: "linear-gradient(135deg,#0052cc 0%,#2684ff 100%)", padding: "18px 22px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h5 style={{ color: "#fff", margin: 0, fontWeight: 700, fontSize: 15 }}>Choose Active Sprint</h5>
              <p style={{ color: "rgba(255,255,255,.78)", margin: "3px 0 0", fontSize: 12 }}>
                Multiple projects have active sprints. Pick the one you want to complete.
              </p>
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#fff", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
              ×
            </button>
          </div>
        </div>

        <div style={{ padding: "18px 22px 14px", maxHeight: "55vh", overflowY: "auto" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {activeSprints.map((sprint) => (
              <button
                key={sprint.id}
                type="button"
                onClick={() => onSelect(sprint)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                  padding: "13px 14px",
                  borderRadius: 10,
                  border: "1px solid #dcfce7",
                  background: "#f0fdf4",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#166534" }}>
                    {sprint.projectName || "Project"} · Sprint {sprint.sprintNumber}
                  </div>
                  <div style={{ fontSize: 12, color: "#374151", marginTop: 3 }}>{sprint.name}</div>
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#166534", whiteSpace: "nowrap" }}>
                  {sprint.totalTasks || 0} tasks
                </div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", padding: "0 22px 20px" }}>
          <button
            onClick={onClose}
            style={{ padding: "9px 20px", borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", cursor: "pointer", fontWeight: 500, fontSize: 13 }}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

/* ── TaskCard (Sprint Board) ───────────────────────────────── */
const TaskCard = ({ task, onRemove, onOpen }) => {
  const isOverdue = task?.dueDate && new Date(task.dueDate) < new Date() && (task.status || "").toUpperCase() !== "COMPLETED";
  return (
    <div className={`task-card-emp ${isOverdue ? "task-card-emp--overdue" : ""}`} onClick={() => onOpen(task)} style={{ position: "relative" }}>
      <div className="task-card-emp__top">
        <div className="task-card-emp__top-row">
          <div className="task-card-emp__project">{task.taskCode || `#${task.id}`}</div>
          {task?.priority
            ? <span className={`task-card-emp__priority ${getPriorityClass(task.priority)}`}>{task.priority}</span>
            : <span className="task-card-emp__priority task-card-emp__priority--none">No Priority</span>}
        </div>
        <div className="task-card-emp__title">{task.title}</div>
      </div>
      <div className="task-card-emp__meta">
        <span className={`task-card-emp__due ${!task?.dueDate ? "task-card-emp__due--na" : isOverdue ? "task-card-emp__due--overdue" : ""}`}>
          <i className="bi bi-calendar-event me-1" />
          {task?.dueDate ? new Date(task.dueDate).toLocaleDateString("en-GB") : "N/A"}
        </span>
        <div className="task-card-emp__assignee">
          {task?.assignedTo ? (
            <>
              <div className={`task-card-emp__avatar ${!task.assignedTo.photoPath ? getAvatarClass(task.id) : ""}`}>
                {task.assignedTo.photoPath
                  ? <img src={`${API_BASE_URL}/uploads/${task.assignedTo.photoPath}`} alt="" onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = "/default-avatar.png"; }} />
                  : getAssigneeInitial(task.assignedTo)}
              </div>
              <span className="task-card-emp__assignee-name">{task.assignedTo.firstName} {task.assignedTo.lastName || ""}</span>
            </>
          ) : (
            <>
              <div className="task-card-emp__avatar task-card-emp__avatar--unassigned"><i className="bi bi-person" /></div>
              <span className="task-card-emp__assignee-name task-card-emp__assignee-name--unassigned">Unassigned</span>
            </>
          )}
        </div>
      </div>
      {/* <button type="button" onClick={e=>{e.stopPropagation();onRemove(task);}} title="Move to backlog"
        style={{ position:"absolute",top:10,right:10,background:"transparent",border:"none",color:"#cbd5e1",fontSize:14,lineHeight:1,cursor:"pointer",padding:2 }}>
        <i className="bi bi-x-lg"/>
      </button> */}
    </div>
  );
};

/* ── SprintKanbanCard ──────────────────────────────────────── */
const SprintKanbanCard = ({ sprint, onView, onEdit, onDelete, onStart, onComplete }) => {
  const sb = STATUS_BADGE[sprint.status] || STATUS_BADGE.PLANNING;
  const progress = pct(sprint);
  const days = daysLeft(sprint);
  return (
    <div onClick={() => onView(sprint)}
      style={{
        background: "#fff", border: "1px solid #e5e7eb", borderLeft: `4px solid ${sb.color}`, borderRadius: 10,
        padding: "14px 14px 12px", marginBottom: 10, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow 0.15s,transform 0.15s"
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.12)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "translateY(0)"; }}>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 10, color: "#9ca3af", fontFamily: "monospace", fontWeight: 600 }}>Sprint {sprint.sprintNumber}</span>
        {sprint.status === "ACTIVE" && days !== null && (
          <span style={{
            fontSize: 10, padding: "2px 6px", borderRadius: 4, fontWeight: 600,
            background: days < 0 ? "#fee2e2" : days <= 2 ? "#fef3c7" : "#f0fdf4",
            color: days < 0 ? "#991b1b" : days <= 2 ? "#92400e" : "#166534"
          }}>
            {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "Ends today" : `${days}d left`}
          </span>
        )}
      </div>

      <p style={{ fontWeight: 700, fontSize: 13, color: "#111827", margin: "0 0 4px", lineHeight: 1.4 }}>{sprint.name}</p>
      {sprint.goal && (
        <p style={{
          fontSize: 11, color: "#6b7280", margin: "0 0 8px", lineHeight: 1.4,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"
        }}>{sprint.goal}</p>
      )}
      <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 8 }}>📅 {fmtShort(sprint.startDate)} – {fmtShort(sprint.endDate)}</div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 3 }}>
          <span style={{ color: "#9ca3af" }}>Progress</span>
          <span style={{ fontWeight: 700, color: "#374151" }}>{progress}%</span>
        </div>
        <div style={{ height: 5, background: "#e5e7eb", borderRadius: 99 }}>
          <div style={{
            height: "100%", borderRadius: 99, width: `${progress}%`,
            background: progress === 100 ? "#16a34a" : "linear-gradient(90deg,#3b82f6,#6366f1)", transition: "width 0.4s"
          }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#9ca3af", marginTop: 2 }}>
          <span>✅ {sprint.completedTasks} done</span>
          <span>⏳ {sprint.pendingTasks} pending</span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 10, paddingTop: 10, borderTop: "1px solid #f0f0f0" }}
        onClick={e => e.stopPropagation()}>
        {sprint.status === "PLANNING" && (
          <button onClick={() => onStart(sprint)} title="Start Sprint"
            style={{ padding: "5px 10px", borderRadius: 6, background: "#16a34a", color: "#fff", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
            <i className="bi bi-play-fill" /> Start
          </button>
        )}
        {sprint.status === "ACTIVE" && (
          <button onClick={() => onComplete(sprint)} title="Complete Sprint"
            style={{ padding: "5px 10px", borderRadius: 6, background: "#6b7280", color: "#fff", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
            <i className="bi bi-check-lg" /> Complete
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => onView(sprint)} title="View Sprint"
          style={{ width: 30, height: 30, borderRadius: 7, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
          <i className="bi bi-eye" />
        </button>
        <button onClick={() => onEdit(sprint)} title="Edit Sprint"
          style={{ width: 30, height: 30, borderRadius: 7, background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
          <i className="bi bi-pencil" />
        </button>
        <button onClick={() => onDelete(sprint)} title="Delete Sprint"
          style={{ width: 30, height: 30, borderRadius: 7, background: "#fff0f0", color: "#dc3545", border: "1px solid #fca5a5", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
          <i className="bi bi-trash" />
        </button>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
══════════════════════════════════════════════════════════════ */
const AdminSprintPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const employeeId = location.state?.employeeId || localStorage.getItem("employeeId");
  const loggedInUserId = localStorage.getItem("userId");
  const loggedInUserName = localStorage.getItem("name");
  const userRoles = JSON.parse(localStorage.getItem("roles") || "[]");
  const isAdmin = userRoles.includes("ROLE_ADMIN");
  const isManager = userRoles.includes("ROLE_MANAGER");

  /* ── Layout ── */
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isProjectOpen, setIsProjectOpen] = useState(true);

  /* ── Projects ── */
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(
    () => localStorage.getItem("selectedProject") || "all"
  );

  /* ── Notifications ── */
  const [showNotifications, setShowNotifications] = useState(false);
  const [auditList, setAuditList] = useState([]);
  const notificationRef = useRef(null);
  const bellRef = useRef(null);

  /* ── Sprint panel internal routing ── */
  // view: "kanban" | "view"
  const [view, setView] = useState("kanban");
  const [editingId, setEditingId] = useState(null);
  const [formMode, setFormMode] = useState(null);

  /* ── Sprint list ── */
  const [sprints, setSprints] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmModal, setConfirmModal] = useState({ open: false, sprint: null });
  const [completeModal, setCompleteModal] = useState({ open: false, sprint: null });
  const [activeSprintPickerOpen, setActiveSprintPickerOpen] = useState(false);
  const [completing, setCompleting] = useState(false);

  /* ── Form ── */
  const [form, setForm] = useState({ name: "", goal: "", status: "PLANNING", startDate: "", endDate: "" });
  const [saving, setSaving] = useState(false);

  /* ── Sprint detail ── */
  const [activeSprint, setActiveSprint] = useState(null);
  const [sprintTasks, setSprintTasks] = useState([]);
  const [burndown, setBurndown] = useState([]);
  const [backlogTasks, setBacklogTasks] = useState([]);
  const [loadingView, setLoadingView] = useState(false);
  const [loadedTabs, setLoadedTabs] = useState(new Set());
  const [boardTab, setBoardTab] = useState("board");
  const [boardSearch, setBoardSearch] = useState("");
  const [taskSearch, setTaskSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selectedBacklog, setSelectedBacklog] = useState(new Set());
  const [selectedList, setSelectedList] = useState(new Set());

  const SERVER_TZ_CUTOFF = new Date("2025-07-01T00:00:00Z");

  /* ══════════════════════════════════════════════════════════
     AUTH GUARD
  ══════════════════════════════════════════════════════════ */
  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) { toast.error("You must be logged in."); navigate("/"); }
  }, [navigate]);

  /* ══════════════════════════════════════════════════════════
     DATA: PROJECTS
  ══════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!token) return;
    const url = isAdmin
      ? `${API_BASE_URL}/api/projects/all`
      : `${API_BASE_URL}/api/projects/my-projects`;
    fetch(url, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : [];
        if (!isAdmin && list.length === 0) { toast.error("Not authorized."); navigate("/"); return; }
        setProjects(list);
      })
      .catch(() => setProjects([]));
  }, [token, isAdmin, navigate]);

  /* ══════════════════════════════════════════════════════════
     DATA: SPRINTS
  ══════════════════════════════════════════════════════════ */
  const effectiveProjectId = selectedProject !== "all" ? selectedProject : null;

  const fetchSprints = useCallback(async () => {
    setLoadingList(true);
    try {
      if (selectedProject === "all") {
        if (!projects.length) {
          setSprints([]);
          return;
        }

        const responses = await Promise.all(
          projects.map(async (project) => {
            let url = `${API_BASE_URL}/api/sprints?projectId=${project.id}`;
            if (statusFilter) url += `&status=${statusFilter}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            return Array.isArray(data)
              ? data.map((sprint) => ({
                ...sprint,
                projectId: sprint.projectId ?? project.id,
                projectName: sprint.projectName || project.name,
              }))
              : [];
          })
        );

        setSprints(responses.flat());
        return;
      }

      if (!effectiveProjectId) {
        setSprints([]);
        return;
      }

      let url = `${API_BASE_URL}/api/sprints?projectId=${effectiveProjectId}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setSprints(Array.isArray(data) ? data : []);
    } catch { toast.error("Failed to load sprints"); }
    finally { setLoadingList(false); }
  }, [effectiveProjectId, projects, selectedProject, statusFilter, token]);

  useEffect(() => { fetchSprints(); }, [fetchSprints]);

  /* reset to kanban when project changes */
  useEffect(() => {
    setView("kanban");
    setFormMode(null);
    setActiveSprint(null);
    setSprintTasks([]);
    setBacklogTasks([]);
    setBurndown([]);
    setEditingId(null);
  }, [selectedProject]);

  /* ══════════════════════════════════════════════════════════
     DATA: SPRINT DETAIL
  ══════════════════════════════════════════════════════════ */
  const fetchSprintDetail = useCallback(async (id, sprintProjectId) => {
    const detailProjectId = sprintProjectId || effectiveProjectId;
    if (!detailProjectId) return;
    setLoadingView(true);
    try {
      const [sRes, tRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/sprints/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/sprints/${id}/tasks`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const [sprint, tasks] = await Promise.all([sRes.json(), tRes.json()]);
      setActiveSprint(sprint);
      setSprintTasks(Array.isArray(tasks) ? tasks.map(normalizeTask) : []);
      setLoadedTabs(new Set(["board", "list"]));
    } catch { toast.error("Failed to load sprint detail"); }
    finally { setLoadingView(false); }
  }, [effectiveProjectId, token]);

  const fetchBurndownData = useCallback(async (id) => {
    setLoadingView(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sprints/${id}/burndown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBurndown(Array.isArray(data) ? data : []);
      setLoadedTabs(prev => new Set([...prev, "burndown"]));
    } catch { toast.error("Failed to load burndown data"); }
    finally { setLoadingView(false); }
  }, [token]);

  const fetchBacklogData = useCallback(async (id, sprintProjectId) => {
    const detailProjectId = sprintProjectId || effectiveProjectId;
    if (!detailProjectId) return;
    setLoadingView(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sprints/backlog?projectId=${detailProjectId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setBacklogTasks(Array.isArray(data) ? data.map(normalizeTask) : []);
      setLoadedTabs(prev => new Set([...prev, "backlog"]));
    } catch { toast.error("Failed to load backlog"); }
    finally { setLoadingView(false); }
  }, [effectiveProjectId, token]);

  const refreshAfterTaskChange = useCallback((id, projectId) => {
    fetchSprintDetail(id, projectId);
    if (loadedTabs.has("backlog")) fetchBacklogData(id, projectId);
  }, [fetchSprintDetail, fetchBacklogData, loadedTabs]);

  /* ══════════════════════════════════════════════════════════
     NOTIFICATIONS
  ══════════════════════════════════════════════════════════ */
  const safeRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    if (d < SERVER_TZ_CUTOFF) return formatDistanceToNow(new Date(d.getTime() + 5.5 * 3600000), { addSuffix: true });
    return formatDistanceToNow(d, { addSuffix: true });
  };

  const isUnread = (a) => {
    if (a.type === "MENTION") return typeof a.isRead === "boolean" ? !a.isRead : true;
    if (!a.readBy || !a.readBy.trim()) return true;
    return !a.readBy.split(",").map(id => id.trim()).includes(String(loggedInUserId));
  };

  const unreadCount = useMemo(() => auditList.filter(isUnread).length, [auditList]);

  const fetchAuditNotifications = useCallback(async () => {
    try {
      const projectIds = selectedProject === "all" ? projects.map(p => p.id) : [Number(selectedProject)];
      if (!projectIds.length) { setAuditList([]); return; }
      const params = new URLSearchParams();
      projectIds.forEach(id => params.append("projectIds", id));

      const [auditRes, mentionRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/tasks/project/audit`, { params, headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/api/tasks/comments/mentions`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const audits = (auditRes.data || []).filter(a => a.changedBy !== loggedInUserName)
        .map(a => ({ ...a, type: "AUDIT", timestamp: a.changedAt }));
      const mentions = (mentionRes.data || []).filter(m => m.author !== loggedInUserName)
        .map(m => ({
          auditId: `mention-${m.commentId}`, commentId: m.commentId, taskId: m.taskId,
          changedBy: m.author, type: "MENTION", text: m.text, isRead: m.isRead, timestamp: m.createdAt
        }));

      const merged = [...audits, ...mentions].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setAuditList(merged.filter(isUnread));
    } catch { setAuditList([]); }
  }, [selectedProject, projects, token, loggedInUserName]);

  useEffect(() => { fetchAuditNotifications(); }, [fetchAuditNotifications]);

  useEffect(() => {
    if (!showNotifications) return;
    fetchAuditNotifications();
    const interval = setInterval(fetchAuditNotifications, 10000);
    return () => clearInterval(interval);
  }, [showNotifications]);

  useEffect(() => {
    const handler = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showNotifications]);

  /* ══════════════════════════════════════════════════════════
     SPRINT ACTIONS
  ══════════════════════════════════════════════════════════ */
  const handleDelete = async (sprint) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sprints/${sprint.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      toast.success(`"${sprint.name}" deleted`);
      fetchSprints();
    } catch { toast.error("Failed to delete sprint"); }
    setConfirmModal({ open: false, sprint: null });
  };

  const handleStart = async (sprint) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sprints/${sprint.id}/start`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || ""); }
      toast.success(`"${sprint.name}" started`);
      fetchSprints();
    } catch (e) { toast.error(e.message || "Cannot start sprint — only one active sprint allowed per project"); }
  };

  const handleConfirmComplete = async (targetSprintId) => {
    setCompleting(true);
    const { sprint } = completeModal;
    try {
      const body = targetSprintId ? { targetSprintId: Number(targetSprintId) } : {};
      const res = await fetch(`${API_BASE_URL}/api/sprints/${sprint.id}/complete`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      toast.success(`"${sprint.name}" completed!`);
      setCompleteModal({ open: false, sprint: null });
      fetchSprints();
      if (view === "view" && editingId === sprint.id) fetchSprintDetail(sprint.id, sprint.projectId);
    } catch { toast.error("Failed to complete sprint"); }
    finally { setCompleting(false); }
  };

  /* ── Form ── */
  const openNew = () => {
    setForm({ name: "", goal: "", status: "PLANNING", startDate: "", endDate: "" });
    setEditingId(null);
    setFormMode("new");
  };
  const openEdit = (sprint) => {
    setEditingId(sprint.id);
    setForm({
      name: sprint.name || "", goal: sprint.goal || "", status: sprint.status || "PLANNING",
      startDate: sprint.startDate || "", endDate: sprint.endDate || ""
    });
    setFormMode("edit");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Sprint name is required"); return; }
    if (form.startDate && form.endDate && form.endDate < form.startDate) { toast.error("End date must be after start date"); return; }
    setSaving(true);
    try {
      const isNew = formMode === "new";

      // ── CREATE IN ALL PROJECTS ──────────────────────────────
      if (isNew && selectedProject === "all") {
        const results = await Promise.allSettled(
          projects.map(project =>
            fetch(`${API_BASE_URL}/api/sprints`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                name: form.name.trim(),
                goal: form.goal.trim(),
                projectId: Number(project.id),
                status: form.status,
                startDate: form.startDate || null,
                endDate: form.endDate || null,
              }),
            })
          )
        );
        const succeeded = results.filter(r => r.status === "fulfilled" && r.value.ok).length;
        const failed = results.length - succeeded;
        if (succeeded > 0) {
          toast.success(`Sprint created in ${succeeded} project${succeeded !== 1 ? "s" : ""}${failed > 0 ? ` (${failed} failed)` : ""}`);
        } else {
          toast.error("Failed to create sprint in any project");
        }
        fetchSprints();
        setFormMode(null);
        return;
      }

      // ── SINGLE PROJECT CREATE OR EDIT ──────────────────────
      const url = isNew
        ? `${API_BASE_URL}/api/sprints`
        : `${API_BASE_URL}/api/sprints/${editingId}`;
      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: form.name.trim(),
          goal: form.goal.trim(),
          projectId: Number(effectiveProjectId),
          status: form.status,
          startDate: form.startDate || null,
          endDate: form.endDate || null,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(isNew ? "Sprint created" : "Sprint updated");
      fetchSprints();
      setFormMode(null);
      if (!isNew && view === "view" && editingId) {
        fetchSprintDetail(editingId, activeSprint?.projectId);
      }
    } catch { toast.error("Failed to save sprint"); }
    finally { setSaving(false); }
  };

  /* ── View actions ── */
  const openView = (sprint) => {
    setEditingId(sprint.id);
    setBoardTab("board");
    setBoardSearch("");
    setTaskSearch("");
    setTypeFilter("");
    setSelectedBacklog(new Set());
    setSelectedList(new Set());
    setBurndown([]);
    setBacklogTasks([]);
    setLoadedTabs(new Set());
    setView("view");
    fetchSprintDetail(sprint.id, sprint.projectId);
  };

  const openTask = (task) => {
    if (!task?.id) return;
    window.open(`${window.location.origin}/tasks?taskId=${task.id}`, "_blank", "noopener,noreferrer");
  };

  const handleRemoveTask = async (task) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sprints/${editingId}/tasks/${task.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      toast.success(`"${task.title}" moved to backlog`);
      refreshAfterTaskChange(editingId, activeSprint?.projectId);
    } catch { toast.error("Failed to remove task"); }
  };

  const handleAddTask = async (task) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sprints/${editingId}/tasks/${task.id}`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      toast.success(`"${task.title}" added to sprint`);
      refreshAfterTaskChange(editingId, activeSprint?.projectId);
    } catch { toast.error("Failed to add task"); }
  };

  const handleAddSelectedTasks = async () => {
    const taskIds = [...selectedBacklog];
    if (!taskIds.length) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/sprints/${editingId}/tasks/bulk`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(taskIds),
      });
      if (!res.ok) throw new Error();
      toast.success(`${taskIds.length} task${taskIds.length !== 1 ? "s" : ""} added`);
      setSelectedBacklog(new Set());
      refreshAfterTaskChange(editingId, activeSprint?.projectId);
    } catch { toast.error("Failed to add tasks"); }
  };

  const handleRemoveSelectedTasks = async () => {
    const taskIds = [...selectedList];
    if (!taskIds.length) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/sprints/${editingId}/tasks/bulk`, {
        method: "DELETE", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(taskIds),
      });
      if (!res.ok) throw new Error();
      toast.success(`${taskIds.length} task${taskIds.length !== 1 ? "s" : ""} moved to backlog`);
      setSelectedList(new Set());
      refreshAfterTaskChange(editingId, activeSprint?.projectId);
    } catch { toast.error("Failed to remove tasks"); }
  };

  const handleViewStart = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sprints/${editingId}/start`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || ""); }
      toast.success("Sprint started!");
      fetchSprintDetail(editingId, activeSprint?.projectId);
      fetchSprints();
    } catch (e) { toast.error(e.message || "Cannot start — only one active sprint per project"); }
  };

  /* ══════════════════════════════════════════════════════════
     STATS (derived from sprints list)
  ══════════════════════════════════════════════════════════ */
  const handleToolbarCompleteActiveSprint = () => {
    if (activeSprints.length === 0) return;
    if (selectedProject === "all" && activeSprints.length > 1) {
      setActiveSprintPickerOpen(true);
      return;
    }
    setCompleteModal({ open: true, sprint: activeSprints[0] });
  };

  const closeFormModal = () => {
    if (saving) return;
    setFormMode(null);
  };

  const stats = useMemo(() => ({
    total: sprints.length,
    planning: sprints.filter(s => s.status === "PLANNING").length,
    active: sprints.filter(s => s.status === "ACTIVE").length,
    completed: sprints.filter(s => s.status === "COMPLETED").length,
  }), [sprints]);

  /* ══════════════════════════════════════════════════════════
     FILTERED SPRINTS (search)
  ══════════════════════════════════════════════════════════ */
  const filteredSprints = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return sprints.filter(s =>
      (!q || s.name?.toLowerCase().includes(q) || s.goal?.toLowerCase().includes(q)) &&
      (!statusFilter || s.status === statusFilter)
    );
  }, [sprints, searchQuery, statusFilter]);

  const groupedProjectSprints = useMemo(() =>
    projects
      .map(project => ({
        project,
        sprints: filteredSprints.filter(s => String(s.projectId) === String(project.id)),
      }))
      .filter(group => group.sprints.length > 0),
    [filteredSprints, projects]);

  const activeSprints = useMemo(
    () => sprints.filter(s => s.status === "ACTIVE"),
    [sprints]
  );

  const renderSprintBoardColumns = (projectSprints) => {
    const groupedByStatus = SPRINT_KANBAN_COLS.reduce((acc, col) => {
      acc[col.key] = projectSprints.filter(sprint => sprint.status === col.key);
      return acc;
    }, {});

    return (
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(240px,1fr))",
        gap: 16,
        alignItems: "start",
        minWidth: 960,
        overflowX: "auto",
      }}>
        {SPRINT_KANBAN_COLS.map(col => {
          const colSprints = groupedByStatus[col.key] || [];
          return (
            <div key={col.key}>
              <div style={{
                display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
                paddingBottom: 10, borderBottom: `2px solid ${col.border}`
              }}>
                <span style={{ fontSize: 16 }}>{col.icon}</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: col.color }}>{col.label}</span>
                <span style={{
                  marginLeft: "auto", fontSize: 11, fontWeight: 700, padding: "2px 8px",
                  borderRadius: 99, background: col.bg, color: col.color, border: `1px solid ${col.border}`
                }}>
                  {colSprints.length}
                </span>
              </div>
              <div style={{
                background: col.bg, borderRadius: 10, padding: "10px 8px",
                minHeight: 200, border: `1px solid ${col.border}`
              }}>
                {colSprints.length === 0
                  ? <p style={{ fontSize: 12, color: "#d1d5db", textAlign: "center", marginTop: 20, fontStyle: "italic" }}>
                    No {col.label.toLowerCase()} sprints
                  </p>
                  : colSprints.map(sprint => (
                    <SprintKanbanCard
                      key={sprint.id}
                      sprint={sprint}
                      onView={openView}
                      onEdit={openEdit}
                      onDelete={s => setConfirmModal({ open: true, sprint: s })}
                      onStart={handleStart}
                      onComplete={s => setCompleteModal({ open: true, sprint: s })}
                    />
                  ))
                }
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════
     PROJECT NAME HELPER
  ══════════════════════════════════════════════════════════ */
  const currentProjectName = selectedProject === "all"
    ? "All Projects"
    : projects.find(p => String(p.id) === String(selectedProject))?.name || "Project";

  /* ══════════════════════════════════════════════════════════
     BACKLOG FILTERS
  ══════════════════════════════════════════════════════════ */
  const filteredBacklog = useMemo(() => {
    const q = taskSearch.toLowerCase();
    return backlogTasks.filter(t =>
      (!q || t.title?.toLowerCase().includes(q) || t.taskCode?.toLowerCase().includes(q)) &&
      (!typeFilter || t.taskType === typeFilter)
    );
  }, [backlogTasks, taskSearch, typeFilter]);

  const backlogTypes = useMemo(() => [...new Set(backlogTasks.map(t => t.taskType).filter(Boolean))], [backlogTasks]);

  /* ══════════════════════════════════════════════════════════
     BREADCRUMB TITLE
  ══════════════════════════════════════════════════════════ */
  const breadcrumb = () => {
    if (view === "view") return activeSprint?.name || "Sprint";
    return null;
  };

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <>
      {/* ── Fixed Navbar ── */}
      <div className="fixed-top">
        {isAdmin ? <AdminNavbar /> : isManager ? <ManagerNavbar /> : <EmployeeNavbar />}
      </div>

      <div className="d-flex flex-column" style={{ height: "100vh", overflow: "auto", paddingTop: "110px" }}>

        {/* ══════════════════════════════════════════════════
            PAGE HEADER
        ══════════════════════════════════════════════════ */}
        <div
          className="emp-page-header"
          style={{ marginLeft: isSidebarCollapsed ? "70px" : "200px", transition: "margin-left 0.25s ease", position: "relative" }}
        >
          <div className="page-header-left">
            <div className="page-header-title-row">
              <i className="bi bi-lightning-charge-fill header-icon" style={{ color: "#7c3aed" }}></i>
              <h4 className="page-title">
                {view === "kanban" ? `${currentProjectName} — Sprints` : breadcrumb()}
              </h4>
            </div>
            <p className="page-subtitle">
              {view === "kanban"
                ? selectedProject === "all"
                  ? "Plan, track and complete sprints across all projects."
                  : "Plan, track and complete sprints across your project."
                : "Manage tasks, view burndown chart, and handle backlog."}
            </p>
          </div>

          <div className="page-actions">
            {/* Notification Bell */}
            <button ref={bellRef} className="notification-btn" onClick={() => setShowNotifications(p => !p)}>
              <i className="bi bi-bell"></i>
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 10 ? "10+" : unreadCount}</span>
              )}
            </button>

            {/* Action button depends on view */}
            {view === "kanban" && (
              <button className="btn-page-create" onClick={openNew}>
                <i className="bi bi-plus-lg"></i>
                New Sprint
              </button>
            )}
            {view === "view" && activeSprint?.status === "PLANNING" && (
              <button className="btn-page-create" onClick={handleViewStart}
                style={{ background: "#16a34a" }}>
                <i className="bi bi-play-fill"></i>
                Start Sprint
              </button>
            )}
            {view === "view" && activeSprint?.status === "ACTIVE" && (
              <button className="btn-page-create" onClick={() => setCompleteModal({ open: true, sprint: activeSprint })}
                style={{ background: "#6b7280" }}>
                <i className="bi bi-check-lg"></i>
                Complete Sprint
              </button>
            )}
          </div>

          {/* Notification Dropdown */}
          {showNotifications && (
            <div ref={notificationRef} className="audit-dropdown jira-style">
              <div className="audit-header">
                <span>Notifications</span>
                <span className="audit-count">{unreadCount}</span>
              </div>
              {auditList.length === 0
                ? <div className="audit-empty">No recent activity</div>
                : auditList.slice(0, 20).map(a => (
                  <div key={a.auditId} className={`audit-item ${isUnread(a) ? "unread" : ""}`}
                    onClick={() => setShowNotifications(false)}>
                    <div className="audit-avatar" style={{ backgroundColor: getAvatarColor(a.changedBy) }}>
                      {getInitials(a.changedBy || "")}
                    </div>
                    <div className="audit-content">
                      <div className="audit-message">
                        {a.type === "MENTION"
                          ? <><strong>{a.changedBy}</strong> mentioned you</>
                          : `${a.changedBy} changed ${a.fieldName}`}
                      </div>
                      <div className="audit-meta">
                        <span className="audit-time">{safeRelativeTime(a.timestamp)}</span>
                      </div>
                    </div>
                    {isUnread(a) && <div className="audit-dot" />}
                  </div>
                ))
              }
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════════════
            STATS BAR  (kanban view only)
        ══════════════════════════════════════════════════ */}
        {view === "kanban" && (
          <div className="stats-bar"
            style={{ marginLeft: isSidebarCollapsed ? "70px" : "200px", transition: "margin-left 0.25s ease" }}>

            <div className="stats-card stats-card--total">
              <div className="stats-card__icon"><i className="bi bi-lightning-charge-fill" style={{ color: "#7c3aed", fontSize: 14 }} /></div>
              <div><div className="stats-card__val">{stats.total}</div><div className="stats-card__lbl">Total Sprints</div></div>
            </div>

            <div className="stats-card stats-card--progress">
              <div className="stats-card__icon"><i className="bi bi-clock" style={{ color: "#1d4ed8", fontSize: 14 }} /></div>
              <div><div className="stats-card__val">{stats.planning}</div><div className="stats-card__lbl">Planning</div></div>
            </div>

            <div className="stats-card stats-card--overdue">
              <div className="stats-card__icon"><i className="bi bi-play-circle-fill" style={{ color: "#16a34a", fontSize: 14 }} /></div>
              <div><div className="stats-card__val">{stats.active}</div><div className="stats-card__lbl">Active</div></div>
            </div>

            <div className="stats-card stats-card--done">
              <div className="stats-card__icon"><i className="bi bi-check-circle-fill" style={{ color: "#22c55e", fontSize: 14 }} /></div>
              <div><div className="stats-card__val">{stats.completed}</div><div className="stats-card__lbl">Completed</div></div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            TOP TOOLBAR  (kanban view only)
        ══════════════════════════════════════════════════ */}
        {view === "kanban" && (
          <div className="top-filters-bar"
            style={{ marginLeft: isSidebarCollapsed ? "70px" : "200px", transition: "margin-left 0.25s ease" }}>
            <div className="top-toolbar-inner">
              <div className="d-flex align-items-center gap-2">
                {/* Search */}
                <div className="search-wrapper">
                  <i className="bi bi-search"></i>
                  <input
                    type="text"
                    placeholder="Search sprints…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Status filter */}
                <select
                  className="form-select form-select-sm"
                  style={{ width: 160, fontSize: 13, borderRadius: 8 }}
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {Object.entries(STATUS_BADGE).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>

                {/* Complete active sprint shortcut */}
                {activeSprints.length > 0 && (
                  <button
                    className="btn btn-sm"
                    style={{ background: "#16a34a", color: "#fff", borderRadius: 8, fontWeight: 600, fontSize: 12, border: "none", padding: "6px 14px" }}
                    onClick={handleToolbarCompleteActiveSprint}
                  >
                    <i className="bi bi-check-lg me-1" />Complete Active Sprint
                  </button>
                )}
              </div>

              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: 12, color: "#9ca3af" }}>
                  {filteredSprints.length} sprint{filteredSprints.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            BODY  (sidebar + main content)
        ══════════════════════════════════════════════════ */}
        <div className="flex-grow-1 d-flex flex-column p-3 scroll-main-content">

          {/* ── Sidebar ── */}
          <aside className={`projects-sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}>
            <div className="projects-sidebar__inner">
              {!isSidebarCollapsed && <div className="sidebar-section-title">PROJECTS</div>}

              <div className="list-group">
                {/* All Projects */}
                <div className="list-group-item parent-item">
                  <i className={`bi ${isProjectOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
                    onClick={e => { e.stopPropagation(); setIsProjectOpen(p => !p); }} />
                  <i className="bi bi-kanban-fill sidebar-icon purple"></i>
                  {!isSidebarCollapsed && (
                    <span
                      className={selectedProject === "all" ? "active-text" : ""}
                      onClick={() => { setSelectedProject("all"); localStorage.setItem("selectedProject", "all"); }}>
                      All Projects
                    </span>
                  )}
                </div>

                {isProjectOpen && projects.map(p => {
                  const count = sprints.filter(s => String(s.projectId) === String(p.id)).length;
                  return (
                    <div key={p.id}
                      className={`list-group-item project-item ${String(selectedProject) === String(p.id) ? "active" : ""}`}
                      onClick={() => { setSelectedProject(String(p.id)); localStorage.setItem("selectedProject", String(p.id)); }}>
                      <i className="bi bi-kanban-fill sidebar-icon" style={{ color: "#7c3aed" }}></i>
                      {!isSidebarCollapsed && (
                        <>
                          <span className="text-truncate">{p.name}</span>
                          {count > 0 && <span className="count-badge">{count}</span>}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {!isSidebarCollapsed && (
                <>
                  <div className="sidebar-section-title mt-3">QUICK LINKS</div>
                  <div className="list-group-item quick-link" onClick={() => navigate(isAdmin ? "/adminviewtasks" : isManager ? "/viewtaskss" : "/viewtasks")}>
                    <i className="bi bi-kanban-fill sidebar-icon green"></i>
                    <span>Task Board</span>
                  </div>
                  {/* <div className="list-group-item quick-link" onClick={() => navigate("/admintimesheetgrid", { state: { employeeId } })}>
                    <i className="bi bi-clock sidebar-icon green"></i>
                    <span>Timesheet</span>
                  </div> */}
                </>
              )}
            </div>
          </aside>

          {/* ── Main Content Area ── */}
          <div className="task-view-container">
            <div className="main-with-sidebar" style={{ paddingRight: 8 }}>

              {/* ════════════════════════════════════════════
                  VIEW: NO PROJECT SELECTED
              ════════════════════════════════════════════ */}
              {!effectiveProjectId && selectedProject !== "all" && view === "kanban" && (
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  minHeight: 400, color: "#9ca3af", textAlign: "center"
                }}>
                  <div style={{ fontSize: 64, marginBottom: 16 }}>⚡</div>
                  <h5 style={{ color: "#374151", fontWeight: 700, marginBottom: 8 }}>Select a Project</h5>
                  <p style={{ fontSize: 14, maxWidth: 340 }}>
                    Choose a project from the sidebar to view and manage its sprints.
                  </p>
                </div>
              )}

              {/* ════════════════════════════════════════════
                  VIEW: KANBAN BOARD OF SPRINTS
              ════════════════════════════════════════════ */}
              {view === "kanban" && (
                <div>
                  {loadingList ? (
                    <div style={{ textAlign: "center", paddingTop: 60 }}>
                      <div className="spinner-border text-primary" />
                    </div>
                  ) : filteredSprints.length === 0 ? (
                    <div style={{ textAlign: "center", paddingTop: 60, color: "#9ca3af" }}>
                      <div style={{ fontSize: 48, marginBottom: 12 }}>⚡</div>
                      <p style={{ margin: 0, fontSize: 14 }}>
                        {sprints.length === 0 ? "No sprints yet — create the first one!" : "No sprints match your filters."}
                      </p>
                      {sprints.length === 0 && (effectiveProjectId || selectedProject === "all") && (
                        <button className="btn btn-primary mt-3" onClick={openNew}>
                          <i className="bi bi-plus-lg me-1" />Create Sprint
                        </button>
                      )}
                    </div>
                  ) : selectedProject === "all" ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                      {groupedProjectSprints.map(({ project, sprints: projectSprints }) => (
                        <section
                          key={project.id}
                          style={{
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: 14,
                            padding: "18px 18px 16px",
                            boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
                            <div>
                              <h5 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>{project.name}</h5>
                              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6b7280" }}>
                                {projectSprints.length} sprint{projectSprints.length !== 1 ? "s" : ""}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedProject(String(project.id));
                                localStorage.setItem("selectedProject", String(project.id));
                              }}
                              style={{
                                padding: "6px 12px",
                                borderRadius: 8,
                                border: "1px solid #dbeafe",
                                background: "#eff6ff",
                                color: "#1d4ed8",
                                cursor: "pointer",
                                fontWeight: 600,
                                fontSize: 12,
                              }}
                            >
                              Open Project
                            </button>
                          </div>
                          {renderSprintBoardColumns(projectSprints)}
                        </section>
                      ))}
                    </div>
                  ) : (
                    renderSprintBoardColumns(filteredSprints)
                  )}
                </div>
              )}

              {/* ════════════════════════════════════════════
                  VIEW: NEW / EDIT FORM
              ════════════════════════════════════════════ */}
              {(formMode === "new" || formMode === "edit") && (
                <>
                  <div onClick={closeFormModal} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000 }} />
                  <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 2010, width: "min(640px,92vw)", maxHeight: "90vh", overflowY: "auto" }}>
                    <div style={{
                      background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
                      boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden"
                    }}>
                      {/* Form header */}
                      <div style={{ background: "linear-gradient(135deg,#0052cc 0%,#2684ff 100%)", padding: "20px 24px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div>
                            <h5 style={{ color: "#fff", margin: 0, fontWeight: 700, fontSize: 16 }}>
                              {formMode === "new" ? "Create New Sprint" : "Edit Sprint"}
                            </h5>
                            <p style={{ color: "rgba(255,255,255,.7)", margin: "4px 0 0", fontSize: 12 }}>
                              {formMode === "new" && selectedProject === "all"
                                ? `⚡ Will be created in all ${projects.length} projects`
                                : currentProjectName}
                            </p>
                          </div>
                          <button type="button" onClick={closeFormModal}
                            style={{
                              background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 32, height: 32,
                              cursor: saving ? "not-allowed" : "pointer", color: "#fff", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                            ✕
                          </button>
                        </div>
                      </div>

                      <form onSubmit={handleSave} style={{ padding: "24px" }}>
                        {/* Name */}
                        <div style={{ marginBottom: 18 }}>
                          <label style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 6, color: "#374151" }}>
                            Sprint Name <span style={{ color: "#dc3545" }}>*</span>
                          </label>
                          <input type="text" required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                            placeholder="e.g. Sprint 3 — Dashboard v2"
                            style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
                          {formMode === "new" && <p style={{ fontSize: 11, color: "#9ca3af", margin: "4px 0 0" }}>Sprint number is auto-assigned.</p>}
                        </div>

                        {/* Goal */}
                        <div style={{ marginBottom: 18 }}>
                          <label style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 6, color: "#374151" }}>Sprint Goal</label>
                          <textarea rows={3} value={form.goal} onChange={e => setForm(p => ({ ...p, goal: e.target.value }))}
                            placeholder="What should the team achieve in this sprint?"
                            style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, resize: "vertical", outline: "none", boxSizing: "border-box" }} />
                        </div>

                        {/* Status (edit only) */}
                        {formMode === "edit" && (
                          <div style={{ marginBottom: 18 }}>
                            <label style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 6, color: "#374151" }}>Status</label>
                            <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}>
                              {Object.entries(STATUS_BADGE).map(([k, v]) => (
                                <option key={k} value={k}>{v.label}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Dates */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 28 }}>
                          <div>
                            <label style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 6, color: "#374151" }}>Start Date</label>
                            <input type="date" value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))}
                              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box" }} />
                          </div>
                          <div>
                            <label style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 6, color: "#374151" }}>End Date</label>
                            <input type="date" value={form.endDate} min={form.startDate || undefined} onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))}
                              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box" }} />
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                          <button type="submit" disabled={saving}
                            style={{
                              padding: "9px 24px", borderRadius: 8, background: saving ? "#93c5fd" : "#0052cc", color: "#fff", border: "none",
                              cursor: saving ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 13
                            }}>
                            {saving ? "Saving..." : formMode === "new" ? "Create Sprint" : "Save Changes"}
                          </button>
                          <button type="button" onClick={closeFormModal}
                            style={{ padding: "9px 20px", borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", cursor: saving ? "not-allowed" : "pointer", fontWeight: 500, fontSize: 13 }}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </>
              )}

              {/* ════════════════════════════════════════════
                  VIEW: SPRINT DETAIL
              ════════════════════════════════════════════ */}
              {view === "view" && (
                <div>
                  {loadingView ? (
                    <div style={{ textAlign: "center", paddingTop: 60 }}>
                      <div className="spinner-border text-primary" />
                    </div>
                  ) : activeSprint && (
                    <div>
                      {/* ── Sprint Info Card ── */}
                      <div style={{
                        background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)", marginBottom: 16, overflow: "hidden"
                      }}>
                        <div style={{ background: "linear-gradient(135deg,#0052cc 0%,#2684ff 100%)", padding: "16px 20px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", fontFamily: "monospace", fontWeight: 600 }}>
                                  Sprint {activeSprint.sprintNumber}
                                </span>
                                <span style={{
                                  fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                                  background: `${STATUS_BADGE[activeSprint.status]?.bg}cc`,
                                  color: STATUS_BADGE[activeSprint.status]?.color
                                }}>
                                  {STATUS_BADGE[activeSprint.status]?.label}
                                </span>
                                {activeSprint.status === "ACTIVE" && daysLeft(activeSprint) !== null && (() => {
                                  const d = daysLeft(activeSprint);
                                  return (
                                    <span style={{
                                      fontSize: 10, padding: "2px 7px", borderRadius: 4, fontWeight: 600,
                                      background: d < 0 ? "#fee2e2" : d <= 2 ? "#fef3c7" : "#f0fdf4",
                                      color: d < 0 ? "#991b1b" : d <= 2 ? "#92400e" : "#166534"
                                    }}>
                                      {d < 0 ? `${Math.abs(d)}d overdue` : d === 0 ? "Ends today" : `${d}d left`}
                                    </span>
                                  );
                                })()}
                              </div>
                              <h5 style={{ color: "#fff", margin: 0, fontWeight: 700, fontSize: 17 }}>{activeSprint.name}</h5>
                              {activeSprint.goal && <p style={{ color: "rgba(255,255,255,0.75)", margin: "4px 0 0", fontSize: 12 }}>{activeSprint.goal}</p>}
                            </div>
                            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                              {activeSprint.status === "PLANNING" && (
                                <button onClick={handleViewStart}
                                  style={{ padding: "7px 14px", borderRadius: 7, background: "rgba(22,163,74,0.9)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                                  ▶ Start
                                </button>
                              )}
                              {activeSprint.status === "ACTIVE" && (
                                <button onClick={() => setCompleteModal({ open: true, sprint: activeSprint })}
                                  style={{ padding: "7px 14px", borderRadius: 7, background: "rgba(107,114,128,0.9)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                                  ✔ Complete
                                </button>
                              )}
                              <button onClick={() => openEdit(activeSprint)}
                                style={{ padding: "7px 12px", borderRadius: 7, background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                                ✏ Edit
                              </button>
                              <button onClick={() => setView("kanban")}
                                style={{ width: 32, height: 32, borderRadius: 7, background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
                                ←
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Progress + stats row */}
                        <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", borderBottom: "1px solid #f0f0f0" }}>
                          {[
                            { label: "Total", val: activeSprint.totalTasks, c: "#374151" },
                            { label: "Done", val: activeSprint.completedTasks, c: "#16a34a" },
                            { label: "In Progress", val: activeSprint.inProgressTasks, c: "#1d4ed8" },
                            { label: "Pending", val: activeSprint.pendingTasks, c: "#f59e0b" },
                          ].map(x => (
                            <div key={x.label} style={{ textAlign: "center", minWidth: 52 }}>
                              <div style={{ fontSize: 20, fontWeight: 800, color: x.c, lineHeight: 1 }}>{x.val || 0}</div>
                              <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>{x.label}</div>
                            </div>
                          ))}
                          <div style={{ flex: 1, minWidth: 160 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 3 }}>
                              <span style={{ color: "#9ca3af" }}>Sprint Progress</span>
                              <span style={{ fontWeight: 700 }}>{pct(activeSprint)}%</span>
                            </div>
                            <div style={{ height: 7, background: "#e5e7eb", borderRadius: 99 }}>
                              <div style={{
                                height: "100%", borderRadius: 99, width: `${pct(activeSprint)}%`,
                                background: pct(activeSprint) === 100 ? "#16a34a" : "linear-gradient(90deg,#3b82f6,#6366f1)", transition: "width 0.5s"
                              }} />
                            </div>
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
                            📅 {fmtDate(activeSprint.startDate)} → {fmtDate(activeSprint.endDate)}
                          </div>
                        </div>
                      </div>

                      {/* ── Tabs ── */}
                      <div style={{
                        display: "flex", gap: 4, background: "#fff", borderRadius: "8px 8px 0 0",
                        border: "1px solid #e5e7eb", borderBottom: "none", padding: "8px 16px 0", marginBottom: 0
                      }}>
                        {[["board", "⊞ Board"], ["list", "☰ List"], ["burndown", "📉 Burndown"], ["backlog", "📋 Backlog"]].map(([k, label]) => (
                          <button key={k} onClick={() => {
                            setBoardTab(k);
                            if (!loadedTabs.has(k)) {
                              if (k === "burndown") fetchBurndownData(editingId);
                              else if (k === "backlog") fetchBacklogData(editingId, activeSprint?.projectId);
                            }
                          }}
                            style={{
                              padding: "7px 16px", borderRadius: "6px 6px 0 0", border: "none", cursor: "pointer",
                              background: boardTab === k ? "#f8fafc" : "transparent",
                              color: boardTab === k ? "#0052cc" : "#6b7280",
                              fontWeight: boardTab === k ? 700 : 500, fontSize: 13,
                              borderBottom: boardTab === k ? "2px solid #0052cc" : "2px solid transparent"
                            }}>
                            {label}
                          </button>
                        ))}
                      </div>

                      {/* ── Tab content ── */}
                      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: "0 0 8px 8px", padding: "16px" }}>

                        {/* BOARD */}
                        {boardTab === "board" && (
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                              <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
                                <i className="bi bi-search" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 13, pointerEvents: "none" }} />
                                <input type="text" placeholder="Search tasks…" value={boardSearch} onChange={e => setBoardSearch(e.target.value)}
                                  style={{ width: "100%", padding: "7px 12px 7px 32px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                                {boardSearch && <button onClick={() => setBoardSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 14, lineHeight: 1, padding: 0 }}><i className="bi bi-x" /></button>}
                              </div>
                              <span style={{ fontSize: 12, color: "#9ca3af" }}>
                                {sprintTasks.filter(t => { const q = boardSearch.toLowerCase(); return !boardSearch || t.title?.toLowerCase().includes(q) || t.taskCode?.toLowerCase().includes(q); }).length} tasks
                              </span>
                            </div>

                            <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 500px)" }}>
                              <div style={{ display: "flex", gap: 12, alignItems: "flex-start", minWidth: "max-content", paddingBottom: 8 }}>
                                {TASK_STATUS_COLS.map(col => {
                                  const colTasks = sprintTasks.filter(t => {
                                    if ((t.status || "").toUpperCase() !== col.key) return false;
                                    if (!boardSearch) return true;
                                    const q = boardSearch.toLowerCase();
                                    return t.title?.toLowerCase().includes(q) || t.taskCode?.toLowerCase().includes(q);
                                  });
                                  return (
                                    <div key={col.key} className="kanban-column-emp">
                                      <div className="kanban-column-emp__header">
                                        <div className="kanban-column-emp__title">
                                          <i className={`bi ${col.icon} me-2`} style={{ color: col.color }} />
                                          {col.label}
                                        </div>
                                        <span className="kanban-column-emp__count">{colTasks.length}</span>
                                      </div>
                                      <div className="kanban-column-emp__body" style={{ minHeight: 180 }}>
                                        {colTasks.length === 0
                                          ? <div className="kanban-column-emp__empty">No tasks</div>
                                          : colTasks.map(t => (
                                            <TaskCard key={t.id} task={t} onRemove={handleRemoveTask} onOpen={openTask} />
                                          ))
                                        }
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* LIST */}
                        {boardTab === "list" && (() => {
                          const q = boardSearch.toLowerCase();
                          const listTasks = sprintTasks.filter(t => !boardSearch || t.title?.toLowerCase().includes(q) || t.taskCode?.toLowerCase().includes(q));
                          return (
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                                <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
                                  <i className="bi bi-search" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 13, pointerEvents: "none" }} />
                                  <input type="text" placeholder="Search tasks…" value={boardSearch} onChange={e => setBoardSearch(e.target.value)}
                                    style={{ width: "100%", padding: "7px 12px 7px 32px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
                                  {boardSearch && <button onClick={() => setBoardSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af" }}><i className="bi bi-x" /></button>}
                                </div>
                                <span style={{ fontSize: 12, color: "#9ca3af" }}>{listTasks.length} task{listTasks.length !== 1 ? "s" : ""}</span>
                                {selectedList.size > 0 && (
                                  <button onClick={handleRemoveSelectedTasks}
                                    style={{ padding: "7px 14px", borderRadius: 7, background: "#be123c", color: "#fff", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
                                    − Remove Selected ({selectedList.size})
                                  </button>
                                )}
                              </div>

                              {listTasks.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
                                  <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
                                  <p style={{ margin: 0, fontSize: 13 }}>{sprintTasks.length === 0 ? "No tasks in this sprint yet" : "No tasks match your search"}</p>
                                </div>
                              ) : (
                                <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", overflow: "hidden", maxHeight: "calc(100vh - 500px)", overflowY: "auto" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                      <tr style={{ background: "#f9fafb" }}>
                                        <th style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0", width: 36 }}>
                                          <input type="checkbox" title="Select all"
                                            checked={listTasks.length > 0 && listTasks.every(t => selectedList.has(t.id))}
                                            onChange={e => {
                                              if (e.target.checked) { setSelectedList(prev => { const n = new Set(prev); listTasks.forEach(t => n.add(t.id)); return n; }); }
                                              else { setSelectedList(prev => { const n = new Set(prev); listTasks.forEach(t => n.delete(t.id)); return n; }); }
                                            }}
                                            style={{ cursor: "pointer", width: 14, height: 14 }} />
                                        </th>
                                        {["Task ID", "Title", "Type", "Priority", "Status", "Assignee", "Due Date", "Action"].map(h => (
                                          <th key={h} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#6b7280", textAlign: "left", borderBottom: "1px solid #f0f0f0" }}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {listTasks.map(t => {
                                        const statusCol = TASK_STATUS_COLS.find(c => normalizeStatusKey(c.key) === normalizeStatusKey(t.status));
                                        const isOverdue = t.dueDate && new Date(t.dueDate) < new Date() && (t.status || "").toUpperCase() !== "COMPLETED";
                                        return (
                                          <tr key={t.id}
                                            style={{ borderBottom: "1px solid #f9fafb", transition: "background 0.1s", background: selectedList.has(t.id) ? "#fff1f2" : "transparent" }}>
                                            <td style={{ padding: "9px 12px" }}>
                                              <input type="checkbox" checked={selectedList.has(t.id)}
                                                onChange={e => { setSelectedList(prev => { const n = new Set(prev); e.target.checked ? n.add(t.id) : n.delete(t.id); return n; }); }}
                                                style={{ cursor: "pointer", width: 14, height: 14 }} />
                                            </td>
                                            <td onClick={() => openTask(t)} style={{ padding: "9px 12px", fontFamily: "monospace", fontSize: 11, color: "#0052cc", cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" }}>{t.taskCode || `#${t.id}`}</td>
                                            <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 500, color: "#111827", maxWidth: 220 }}>{t.title}</td>
                                            <td style={{ padding: "9px 12px" }}><span style={{ fontSize: 11, padding: "2px 6px", borderRadius: 4, background: "#f3f4f6", color: "#374151" }}>{t.taskType || "—"}</span></td>
                                            <td style={{ padding: "9px 12px" }}>
                                              {t.priority ? <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: `${PRIORITY_COLOR[t.priority] || "#6b7280"}18`, color: PRIORITY_COLOR[t.priority] || "#6b7280" }}>{t.priority}</span> : "—"}
                                            </td>
                                            <td style={{ padding: "9px 12px" }}>
                                              <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap", background: statusCol ? `${statusCol.color}18` : "#f3f4f6", color: statusCol ? statusCol.color : "#6b7280" }}>
                                                {statusCol ? statusCol.label : (t.status || "—")}
                                              </span>
                                            </td>
                                            <td style={{ padding: "9px 12px" }}>
                                              {t.assignedTo ? (
                                                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                                  {t.assignedTo.photoPath
                                                    ? <img src={`${API_BASE_URL}/uploads/${t.assignedTo.photoPath}`} alt="" onError={e => { e.currentTarget.onerror = null; e.currentTarget.style.display = "none"; }} style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                                                    : <div style={{ width: 24, height: 24, borderRadius: "50%", background: getAvatarColor(`${t.assignedTo.firstName} ${t.assignedTo.lastName || ""}`), color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{getInitials(`${t.assignedTo.firstName} ${t.assignedTo.lastName || ""}`)}</div>
                                                  }
                                                  <span style={{ fontSize: 12, color: "#374151" }}>{t.assignedTo.firstName} {t.assignedTo.lastName || ""}</span>
                                                </div>
                                              ) : <span style={{ fontSize: 12, color: "#9ca3af" }}>—</span>}
                                            </td>
                                            <td style={{ padding: "9px 12px", fontSize: 12, color: isOverdue ? "#ef4444" : "#6b7280", fontWeight: isOverdue ? 600 : 400 }}>
                                              {t.dueDate ? new Date(t.dueDate).toLocaleDateString("en-GB") : "—"}
                                            </td>
                                            <td style={{ padding: "9px 12px" }}>
                                              <button type="button" onClick={e => { e.stopPropagation(); handleRemoveTask(t); }}
                                                style={{ padding: "5px 10px", borderRadius: 6, background: "#fff1f2", color: "#be123c", border: "1px solid #fecdd3", cursor: "pointer", fontWeight: 600, fontSize: 11 }}>
                                                Remove
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* BURNDOWN */}
                        {boardTab === "burndown" && (
                          <div>
                            <h6 style={{ fontWeight: 700, margin: "0 0 4px", fontSize: 14 }}>📉 Burndown Chart</h6>
                            <p style={{ color: "#9ca3af", fontSize: 12, margin: "0 0 20px" }}>Ideal vs. actual remaining effort (hours)</p>
                            {burndown.length === 0 ? (
                              <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
                                <div style={{ fontSize: 36, marginBottom: 8 }}>📊</div>
                                <p style={{ margin: 0, fontSize: 13 }}>No data — set sprint dates and add tasks with estimated hours.</p>
                              </div>
                            ) : (
                              <ResponsiveContainer width="100%" height={320}>
                                <LineChart data={burndown} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                  <XAxis dataKey="date" tick={{ fontSize: 10 }}
                                    tickFormatter={v => new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} />
                                  <YAxis tick={{ fontSize: 10 }} unit="h" />
                                  <Tooltip
                                    formatter={(val, name) => [`${val}h`, name === "ideal" ? "Ideal" : "Remaining"]}
                                    labelFormatter={v => new Date(v).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} />
                                  <Legend formatter={v => v === "ideal" ? "Ideal" : "Remaining"} />
                                  <Line type="monotone" dataKey="ideal" stroke="#d1d5db" strokeDasharray="6 3" strokeWidth={2} dot={false} />
                                  <Line type="monotone" dataKey="remaining" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                </LineChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        )}

                        {/* BACKLOG */}
                        {boardTab === "backlog" && (
                          <div>
                            <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
                              <input type="text" placeholder="Search tasks…" value={taskSearch} onChange={e => setTaskSearch(e.target.value)}
                                style={{ width: 220, minWidth: 120, padding: "7px 12px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13 }} />
                              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                                style={{ padding: "7px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13 }}>
                                <option value="">All Types</option>
                                {backlogTypes.map(t => <option key={t}>{t}</option>)}
                              </select>
                              <span style={{ fontSize: 12, color: "#9ca3af" }}>{filteredBacklog.length} task{filteredBacklog.length !== 1 ? "s" : ""}</span>
                              {selectedBacklog.size > 0 && (
                                <button onClick={handleAddSelectedTasks}
                                  style={{ padding: "7px 14px", borderRadius: 7, background: "#0052cc", color: "#fff", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
                                  + Add Selected ({selectedBacklog.size})
                                </button>
                              )}
                            </div>

                            {filteredBacklog.length === 0 ? (
                              <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
                                <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                                <p style={{ margin: 0, fontSize: 13 }}>
                                  {backlogTasks.length === 0 ? "No backlog tasks — all tasks are in sprints!" : "No tasks match your filters"}
                                </p>
                              </div>
                            ) : (
                              <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", overflow: "hidden", maxHeight: "calc(100vh - 500px)", overflowY: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                  <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                    <tr style={{ background: "#f9fafb" }}>
                                      <th style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0", width: 36 }}>
                                        <input type="checkbox" title="Select all"
                                          checked={filteredBacklog.length > 0 && filteredBacklog.every(t => selectedBacklog.has(t.id))}
                                          onChange={e => {
                                            if (e.target.checked) { setSelectedBacklog(prev => { const n = new Set(prev); filteredBacklog.forEach(t => n.add(t.id)); return n; }); }
                                            else { setSelectedBacklog(prev => { const n = new Set(prev); filteredBacklog.forEach(t => n.delete(t.id)); return n; }); }
                                          }}
                                          style={{ cursor: "pointer", width: 14, height: 14 }} />
                                      </th>
                                      {["Task ID", "Title", "Type", "Priority", "Status", "Assignee", "Due Date", "Action"].map(h => (
                                        <th key={h} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 600, color: "#6b7280", textAlign: "left", borderBottom: "1px solid #f0f0f0" }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredBacklog.map(t => (
                                      <tr key={t.id} style={{ borderBottom: "1px solid #f9fafb", background: selectedBacklog.has(t.id) ? "#eff6ff" : "transparent" }}>
                                        <td style={{ padding: "9px 12px" }}>
                                          <input type="checkbox" checked={selectedBacklog.has(t.id)}
                                            onChange={e => { setSelectedBacklog(prev => { const n = new Set(prev); e.target.checked ? n.add(t.id) : n.delete(t.id); return n; }); }}
                                            style={{ cursor: "pointer", width: 14, height: 14 }} />
                                        </td>
                                        <td onClick={() => openTask(t)} style={{ padding: "9px 12px", fontFamily: "monospace", fontSize: 11, color: "#0052cc", cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" }}>{t.taskCode || `#${t.id}`}</td>
                                        <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 500, color: "#111827", maxWidth: 200 }}>{t.title}</td>
                                        <td style={{ padding: "9px 12px" }}><span style={{ fontSize: 11, padding: "2px 6px", borderRadius: 4, background: "#f3f4f6", color: "#374151" }}>{t.taskType || "—"}</span></td>
                                        <td style={{ padding: "9px 12px" }}>
                                          {t.priority ? <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: `${PRIORITY_COLOR[t.priority] || "#6b7280"}18`, color: PRIORITY_COLOR[t.priority] || "#6b7280" }}>{t.priority}</span> : "—"}
                                        </td>
                                        <td style={{ padding: "9px 12px" }}>
                                          {(() => {
                                            const col = TASK_STATUS_COLS.find(c => c.key === (t.status || "").toUpperCase().replace(/ /g, "_")) || TASK_STATUS_COLS.find(c => normalizeStatusKey(c.key) === normalizeStatusKey(t.status));
                                            return <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 4, background: col ? `${col.color}18` : "#f3f4f6", color: col ? col.color : "#6b7280", whiteSpace: "nowrap" }}>{col ? col.label : (t.status || "—")}</span>;
                                          })()}
                                        </td>
                                        <td style={{ padding: "9px 12px" }}>
                                          {t.assignedTo ? (
                                            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                              {t.assignedTo.photoPath
                                                ? <img src={`${API_BASE_URL}/uploads/${t.assignedTo.photoPath}`} alt="" onError={e => { e.currentTarget.onerror = null; e.currentTarget.style.display = "none"; }} style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                                                : <div style={{ width: 24, height: 24, borderRadius: "50%", background: getAvatarColor(`${t.assignedTo.firstName} ${t.assignedTo.lastName || ""}`), color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{getInitials(`${t.assignedTo.firstName} ${t.assignedTo.lastName || ""}`)}</div>
                                              }
                                              <span style={{ fontSize: 12, color: "#374151" }}>{t.assignedTo.firstName} {t.assignedTo.lastName || ""}</span>
                                            </div>
                                          ) : <span style={{ fontSize: 12, color: "#9ca3af" }}>—</span>}
                                        </td>
                                        <td style={{
                                          padding: "9px 12px", fontSize: 12,
                                          color: t.dueDate && new Date(t.dueDate) < new Date() ? "#ef4444" : "#6b7280",
                                          fontWeight: t.dueDate && new Date(t.dueDate) < new Date() ? 600 : 400
                                        }}>
                                          {t.dueDate ? new Date(t.dueDate).toLocaleDateString("en-GB") : "—"}
                                        </td>
                                        {/* <td style={{ padding:"9px 12px",fontSize:12,color:"#6b7280" }}>{t.estimatedTime?`${t.estimatedTime}h`:"—"}</td> */}
                                        <td style={{ padding: "9px 12px" }}>
                                          <button onClick={() => handleAddTask(t)}
                                            style={{ padding: "4px 12px", borderRadius: 6, background: "#0052cc", color: "#fff", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 11 }}>
                                            + Add
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}

                      </div>{/* end tab content */}
                    </div>
                  )}
                </div>
              )}

            </div>{/* end main-with-sidebar */}
          </div>{/* end task-view-container */}
        </div>{/* end flex-grow-1 body */}
      </div>{/* end outer wrapper */}

      {/* ── Modals ── */}
      <ConfirmModal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, sprint: null })}
        onConfirm={() => handleDelete(confirmModal.sprint)}
        title="Delete Sprint"
        message={`Delete "${confirmModal.sprint?.name}"? All tasks will be moved back to the backlog.`}
      />
      <CompleteSprintModal
        open={completeModal.open}
        onClose={() => !completing && setCompleteModal({ open: false, sprint: null })}
        sprint={completeModal.sprint}
        availableSprints={sprints.filter(s =>
          s.id !== completeModal.sprint?.id &&
          s.status !== "COMPLETED" &&
          s.status !== "CANCELLED" &&
          String(s.projectId) === String(completeModal.sprint?.projectId)
        )}
        onConfirm={handleConfirmComplete}
        completing={completing}
      />
      <ActiveSprintPickerModal
        open={activeSprintPickerOpen}
        onClose={() => setActiveSprintPickerOpen(false)}
        activeSprints={activeSprints}
        onSelect={(sprint) => {
          setActiveSprintPickerOpen(false);
          setCompleteModal({ open: true, sprint });
        }}
      />
    </>
  );
};

export default AdminSprintPage;

